import { UiState } from "@/components/enums/UiState";
import ButtonChat from "@/components/shared/futureChat/buttons/ButtonChat";
import Card from "@/components/shared/futureChat/cards/Card";
import CardBulletPoint from "@/components/shared/futureChat/cards/CardBulletPoint";
import { TrackingContext } from "@/components/shared/tracking/Tracking";
import { cn } from "@/lib/utils";
import { inject, observer } from "mobx-react";
import ExportedImage from "next-image-export-optimizer";
import { useContext, useEffect, useState } from "react";
import store from "stores/store";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";

type PrivacyPolicyProps = {
    onClose: () => void;
    className?: string;
};

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onClose, className }) => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const { chatbotLocalized, globalLocalized, privacyPolicyLocalized } = store;

    const [windowWidth, setWindowWidth] = useState(0);

    const { screenView } = useContext(TrackingContext);

    useEffect(() => {
        setWindowWidth(window.innerWidth);

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);

        screenView("X.3 - Privacy Policy");
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const getImageSize = () => {
        if (windowWidth < 480) {
            return chatbotLocalized?.keyvisualDark320px;
        } else if (windowWidth < 768) {
            return chatbotLocalized?.keyvisualDark480px;
        } else if (windowWidth < 1024) {
            return chatbotLocalized?.keyvisualDark768px;
        } else if (windowWidth < 1280) {
            return chatbotLocalized?.keyvisualDark1024px;
        } else if (windowWidth < 1920) {
            return chatbotLocalized?.keyvisualDark1280px;
        } else if (windowWidth >= 1920) {
            return chatbotLocalized?.keyvisualDark1920px;
        }
    };

    const background = getImageSize();

    return (
        <div className="flex h-full w-full flex-col items-center">
            {background !== undefined && background !== null && (
                <div className={cn("absolute h-full w-full")}>
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(background)}
                        alt={getAltTextFromGeneralImage(background)}
                        layout="fill"
                        objectFit="cover"
                    />
                </div>
            )}
            {globalLocalized?.logo !== undefined && globalLocalized?.logo !== null && (
                <div className={cn("relative h-[158px] w-[247px]", "2xs:h-[237px] 2xs:w-[371px]")}>
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(globalLocalized.logo)}
                        alt={getAltTextFromGeneralImage(globalLocalized.logo)}
                        layout="fill"
                        objectFit="contain"
                    />
                </div>
            )}
            <div className={cn("absolute bottom-[46px] flex w-full max-w-[364px] flex-col gap-[12px] px-[16px]")}>
                <Card
                    className={cn(
                        "max-h-[317px] overflow-y-scroll bg-whiteShade-10",
                        "2xs:max-h-max 2xs:overflow-y-visible 2xs:px-[38px] 2xs:py-[28px]",
                    )}
                >
                    <div
                        className={cn(
                            "hyphens-auto break-words font-frutigerBlackItalic text-[18px] font-black uppercase italic leading-[135%] tracking-minus-2 text-shadow-card-shared-text-stele lg:text-[22px]",
                        )}
                    >
                        {privacyPolicyLocalized?.cardTitle}
                    </div>
                    <div
                        className={cn(
                            "font-frutigerRoman text-[13px] leading-[135%] tracking-minus-2 lg:text-[16px]",
                            "2xs:text-[13px]",
                        )}
                    >
                        {privacyPolicyLocalized?.cardSubtitle}
                    </div>
                    <div className={cn("flex flex-col gap-[8px]", "2xs:gap-[10px]")}>
                        {privacyPolicyLocalized?.cardBulletPoints.map(
                            (bulletpoint, bulletpointIndex) =>
                                bulletpoint?.icon !== undefined &&
                                bulletpoint?.icon !== null &&
                                bulletpoint.text !== undefined && (
                                    <CardBulletPoint
                                        key={bulletpointIndex}
                                        icon={
                                            <ExportedImage
                                                src={getImageUrlFromGeneralImage(bulletpoint?.icon)}
                                                alt={getAltTextFromGeneralImage(bulletpoint?.icon)}
                                                layout="fill"
                                                objectFit="cover"
                                            />
                                        }
                                        variant="medium"
                                    >
                                        {bulletpoint?.text}
                                    </CardBulletPoint>
                                ),
                        )}
                    </div>
                    <div
                        className={cn(
                            "font-frutigerRoman text-[13px] leading-[135%] tracking-minus-2 lg:text-[16px]",
                            "2xs:text-[13px]",
                        )}
                    >
                        <p>
                            {privacyPolicyLocalized?.cardContent + " "}
                            <a
                                className="break-words font-bold underline"
                                href="https://www.neueleben.de/datenschutz.html"
                                target="_blanc"
                            >
                                https://www.neueleben.de/datenschutz.html
                            </a>
                        </p>
                    </div>
                </Card>
                <ButtonChat
                    iconRight={
                        <ExportedImage
                            src={
                                privacyPolicyLocalized?.button.icon !== undefined &&
                                privacyPolicyLocalized?.button.icon !== null
                                    ? getImageUrlFromGeneralImage(privacyPolicyLocalized.button.icon)
                                    : "No image provided"
                            }
                            alt={
                                privacyPolicyLocalized?.button.icon !== undefined &&
                                privacyPolicyLocalized?.button.icon !== null
                                    ? getAltTextFromGeneralImage(privacyPolicyLocalized.button.icon)
                                    : "No image provided"
                            }
                            layout="fill"
                            objectPosition="center"
                        />
                    }
                    variant="secondary"
                    onClick={onClose}
                >
                    {privacyPolicyLocalized?.button.text}
                </ButtonChat>
            </div>
        </div>
    );
};
export default inject("store")(observer(PrivacyPolicy));
